// Single source of truth for the tradie minimum-age rule on the client.
//
// Mirrors the App\Rules\MinimumAge rule in tradiepass-api, applied to the
// same three places so they can't contradict each other: the date picker
// (can't select a disallowed date), form submit (blocks regardless of the
// "I confirm I am 16+" checkbox), and the API (real enforcement — the
// client-side checks are just earlier, friendlier feedback).

export const MIN_TRADIE_AGE = 16
export const MAX_BIRTHDATE_AGE_YEARS = 100

/**
 * The most recent date of birth that still meets the minimum age today —
 * i.e. the datepicker's `:max`. Someone born after this date is under 16.
 * @returns {Date}
 */
export function latestAllowedBirthdate(minAge = MIN_TRADIE_AGE) {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setFullYear(d.getFullYear() - minAge)
  return d
}

/**
 * The oldest date of birth allowed — i.e. the datepicker's `:min`. Guards
 * against obviously-wrong dates (typoed birth years) rather than a real
 * age limit.
 * @returns {Date}
 */
export function earliestAllowedBirthdate(
  maxAgeYears = MAX_BIRTHDATE_AGE_YEARS,
) {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setFullYear(d.getFullYear() - maxAgeYears)
  return d
}

/**
 * Pulls the calendar Y/M/D out of a date value without going through a
 * timezone-sensitive `Date` parse. A bare "YYYY-MM-DD" string (what
 * b-form-datepicker's v-model holds) is parsed by `new Date(...)` as UTC
 * midnight, while everywhere else here builds dates in local time — in any
 * timezone ahead of UTC that mismatch is enough to reject someone who
 * turns the minimum age on the exact day. Comparing Y/M/D components
 * directly sidesteps the whole timezone question.
 * @returns {{y: number, m: number, d: number}|null} m is 0-indexed
 */
function toLocalYmd(value) {
  if (!value) return null
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value))
  if (isoMatch) {
    return {
      y: Number(isoMatch[1]),
      m: Number(isoMatch[2]) - 1,
      d: Number(isoMatch[3]),
    }
  }
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return { y: d.getFullYear(), m: d.getMonth(), d: d.getDate() }
}

/**
 * Whether a given date of birth (string or Date) indicates an age of at
 * least `minAge` years, as of today. Returns false for an empty/invalid
 * value so callers can't accidentally treat "no date entered" as passing.
 * @param {string|Date|null|undefined} birthdate
 * @returns {boolean}
 */
export function isOldEnough(birthdate, minAge = MIN_TRADIE_AGE) {
  const dob = toLocalYmd(birthdate)
  if (!dob) return false

  const today = new Date()
  let age = today.getFullYear() - dob.y
  const hadBirthdayThisYear =
    today.getMonth() > dob.m ||
    (today.getMonth() === dob.m && today.getDate() >= dob.d)
  if (!hadBirthdayThisYear) age -= 1

  return age >= minAge
}
